import {put, call} from 'redux-saga/effects';
import Document from "../../entities/document";
import * as Actions from "../actions/documentActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DocumentAction = {
    documentId: string;
    document: Document;
    documents: Document[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDocument(action: DocumentAction) {
	const session = tabSession.captureGeneration();
	try {
		const document: Document = yield call(api.findOne, 'document', action.documentId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_FETCH_ONE_SUCCESS, documentId: action.documentId, document});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCUMENT_FETCH_ONE_FAILURE, documentId: action.documentId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DocumentAction} action
 */
export function* saveDocument(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ DOCUMENT_SAVE saga', action.document?.id);
    
	if (action.document === undefined || action.document === null) {
		console.error('saveDocument received a null');
		throw new Error('saveDocument received a null');
	}

	if (!(action.document instanceof Document)) {
		action.document = new Document(action.document);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'document', action.document);
		if (addResult.isErr()) {
			console.error(`⮂ saveDocument: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.DOCUMENT_SAVE_FAILURE, document: action.document, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_SAVE, document: action.document});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.DOCUMENT_SAVE_FAILURE, document: action.document, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {DocumentAction} action
 * @param {Document[]} action.documents
 */
export function* saveManyDocuments(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ DOCUMENT_SAVE_MANY saga', action.documents.map(document => document.id).join(','));
    
    if (!Array.isArray(action.documents)) {
        console.error('saveManyDocuments expected an array, received', action.documents);
        throw new Error('saveManyDocuments expected an array');
    }

	try {
		const toSave: Document[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.documents.length; i++) {
			const document = action.documents[i];
			document.persisted = false;
			toSave.push(document);
		}

		const addResult = yield call(idb.add, 'document', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyDocuments: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.DOCUMENT_SAVE_MANY_FAILURE, documents: action.documents, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_SAVE_MANY, documents: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.DOCUMENT_SAVE_MANY_FAILURE, documents: action.documents, status: 500, err});

		console.error(`Error saving ${action.documents.length} documents to database`, err);
	}
}

/**
 * @param {DocumentAction} action
 */
export function* removeDocument(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.document === undefined || action.document === null || !(action.document instanceof Document)) {
			console.error('removeDocument expected type Document, received', action.document);
			throw new Error('removeDocument expected type Document');
		}

	try {
		// Mark as deleted
		action.document.status = 'D';
		action.document.addedOn = newHRTimestamp();
		action.document.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'document', action.document);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDocument: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCUMENT_REMOVE_FAILURE, document: action.document, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_REMOVE, document: action.document});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCUMENT_REMOVE_FAILURE, document: action.document, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Document.
	*
	* @param {DocumentAction} action
	*/
export function* restoreDocument(action: DocumentAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDocument expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.document === undefined || action.document === null) {
        console.error('restoreDocument expected a Document, received null');
        throw new Error('restoreDocument expected a Document, received null');
    }
    else if (!(action.document instanceof Document)) action.document = new Document(action.document); 
    
	try {
		// Mark as restored
		action.document.status = 'U';
		action.document.addedOn = newHRTimestamp();
		action.document.addedBy = userId;
		action.document.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'document', action.document);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDocument: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCUMENT_RESTORE_FAILURE, document: action.document, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCUMENT_RESTORE, document: action.document});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCUMENT_RESTORE_FAILURE, document: action.document, status: 500, err});

		console.error('Error restoring from database', err);
	}
}